import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { useNotification } from '../../context/NotificationContext';
import { useUsers } from '../../context/UsersContext';
import { Trash2, Edit } from 'lucide-react';
import PermissionGuard from '../../components/PermissionGuard';
import { usePermissions, PERMISSIONS } from '../../hooks/usePermissions';
import Tooltip from '../../components/Tooltip';
import UserTimestamp from '../../components/UserTimestamp';
import './Admin.css';

/** Friendly labels for known enum values; unknown types use snake_case → Title Case */
const METADATA_TYPE_LABELS = {
  CHECK_TYPE: 'Check Type',
  WORKFLOW_STATUS: 'Workflow Status',
  CLARIFICATION_TYPE: 'Clarification Type',
  BATCH_VALIDATION_ERR: 'Batch Validation Error',
};

const humanizeMetadataTypeEnum = (type) =>
  type
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');

const MasterData = () => {
  const { showError } = useNotification();
  const { can } = usePermissions();
  const { getUserName, getUserById } = useUsers();
  const [masterData, setMasterData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMetadataType, setSelectedMetadataType] = useState('All Types');
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    metadataType: '',
    code: '',
    name: '',
    description: '',
    sortOrder: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdateItem, setStatusUpdateItem] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [supportedMetadataTypes, setSupportedMetadataTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchMasterData(), fetchSupportedMetadataTypes()]);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const data = await api.getMasterData();
      setMasterData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching master data:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch master data';
      showError(errorMessage);
      setMasterData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportedMetadataTypes = async () => {
    try {
      setTypesLoading(true);
      const data = await api.getMasterDataTypes();
      const list = Array.isArray(data) ? data.filter((t) => typeof t === 'string' && t.trim()) : [];
      setSupportedMetadataTypes(list);
    } catch (error) {
      console.error('Error fetching master data types:', error);
      const errorMessage =
        error?.message || error?.data?.message || 'Failed to fetch master data types';
      showError(errorMessage);
      setSupportedMetadataTypes([]);
    } finally {
      setTypesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value
      .toUpperCase()
      .replace(/\s/g, '')
      .replace(/[^A-Z0-9_]/g, '');
    setFormData((prev) => ({ ...prev, code: value }));
    if (formErrors.code) {
      setFormErrors((prev) => ({ ...prev, code: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Only validate metadataType and code in create mode
    if (!editMode) {
      if (!formData.metadataType) {
        errors.metadataType = 'Metadata type is required';
      }

      if (!formData.code.trim()) {
        errors.code = 'Code is required';
      } else if (!/^[A-Z0-9_]+$/.test(formData.code)) {
        errors.code = 'Code must be uppercase letters and underscores only';
      }
    }

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    if (formData.sortOrder !== '' && formData.sortOrder !== null && formData.sortOrder !== undefined) {
      const num = Number(formData.sortOrder);
      if (isNaN(num) || num < 0) {
        errors.sortOrder = 'Sort order must be a non-negative number';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      
      if (editMode && editingItem) {
        // Update mode - only send name, description, sortOrder
        const payload = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          sortOrder: formData.sortOrder === '' ? 0 : Number(formData.sortOrder),
        };
        await api.updateMasterData(editingItem.uuid, payload);
      } else {
        // Create mode - send all fields
        const payload = {
          metadataType: formData.metadataType,
          code: formData.code.trim(),
          name: formData.name.trim(),
          description: formData.description.trim(),
          sortOrder: formData.sortOrder === '' ? 0 : Number(formData.sortOrder),
          isActive: formData.isActive,
        };
        await api.createMasterData(payload);
      }
      
      setShowAddModal(false);
      resetForm();
      await fetchMasterData();
    } catch (error) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} master data:`, error);
      const errorMessage = error?.message || error?.data?.message || `Failed to ${editMode ? 'update' : 'create'} master data`;
      setFormErrors({ submit: errorMessage });
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      metadataType: '',
      code: '',
      name: '',
      description: '',
      sortOrder: '',
      isActive: true,
    });
    setFormErrors({});
    setEditMode(false);
    setEditingItem(null);
  };

  const handleStatusToggle = (item) => {
    setStatusUpdateItem(item);
    setShowStatusModal(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!statusUpdateItem) return;

    try {
      setIsUpdatingStatus(true);
      await api.updateMasterDataActive(statusUpdateItem.uuid, !statusUpdateItem.isActive);
      await fetchMasterData();
      setShowStatusModal(false);
      setStatusUpdateItem(null);
    } catch (error) {
      console.error('Error updating status:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to update status';
      showError(errorMessage);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const handleEditClick = (item) => {
    setEditMode(true);
    setEditingItem(item);
    setFormData({
      metadataType: item.metadataType,
      code: item.code,
      name: item.name,
      description: item.description,
      sortOrder: item.sortOrder != null ? item.sortOrder : '',
      isActive: item.isActive,
    });
    setShowAddModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem) return;

    try {
      setIsDeleting(true);
      await api.deleteMasterData(deleteItem.uuid);
      await fetchMasterData();
      setShowDeleteModal(false);
      setDeleteItem(null);
    } catch (error) {
      console.error('Error deleting master data:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to delete master data';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatMetadataType = (type) => {
    if (!type) return '-';
    if (METADATA_TYPE_LABELS[type]) return METADATA_TYPE_LABELS[type];
    return humanizeMetadataTypeEnum(type);
  };

  // Truncate text and show tooltip on hover
  const TruncatedText = ({ text, maxLength = 30 }) => {
    if (!text) return <span>-</span>;
    
    const shouldTruncate = text.length > maxLength;
    const displayText = shouldTruncate ? text.substring(0, maxLength) + '...' : text;
    
    if (shouldTruncate) {
      return (
        <Tooltip text={text} position="top">
          <span className="truncated-text">
            {displayText}
          </span>
        </Tooltip>
      );
    }
    
    return <span>{displayText}</span>;
  };

  const filterTypeOptions = useMemo(() => {
    if (supportedMetadataTypes.length > 0) {
      return ['All Types', ...supportedMetadataTypes];
    }
    const fromData = [...new Set(masterData.map((item) => item.metadataType).filter(Boolean))];
    return ['All Types', ...fromData];
  }, [supportedMetadataTypes, masterData]);

  useEffect(() => {
    if (
      selectedMetadataType !== 'All Types' &&
      !filterTypeOptions.includes(selectedMetadataType)
    ) {
      setSelectedMetadataType('All Types');
    }
  }, [filterTypeOptions, selectedMetadataType]);

  const filteredData = masterData.filter((item) => {
    const matchesSearch =
      !searchTerm ||
      (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType =
      selectedMetadataType === 'All Types' || item.metadataType === selectedMetadataType;

    return matchesSearch && matchesType;
  });

  return (
    <div className="admin-page user-management-page master-data-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Master Data</h1>
          <p className="page-subtitle">
            Manage metadata items for Check Types, Workflow Status, and Clarifications Type.
          </p>
        </div>
        <PermissionGuard permission={PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW}>
          <button className="btn-add-user" onClick={() => setShowAddModal(true)}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M10 3V17M3 10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add Master Data
          </button>
        </PermissionGuard>
      </div>

      <div className="filters-section">
        <div className="search-bar-wrapper">
          <div className="search-bar">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
              <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search master data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="filter-dropdowns-wrapper">
          <select
            className="filter-select"
            value={selectedMetadataType}
            onChange={(e) => setSelectedMetadataType(e.target.value)}
          >
            {filterTypeOptions.map((type) => (
              <option key={type} value={type}>
                {type === 'All Types' ? 'All Types' : formatMetadataType(type)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-section">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            Loading master data...
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Sort Order</th>
                  <th>Active</th>
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                      No master data found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.uuid}>
                      <td>{formatMetadataType(item.metadataType)}</td>
                      <td>{item.code || '-'}</td>
                      <td><TruncatedText text={item.name} maxLength={20} /></td>
                      <td><TruncatedText text={item.description} maxLength={30} /></td>
                      <td>{item.sortOrder != null ? item.sortOrder : '-'}</td>
                      <td>
                        <PermissionGuard permission={PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW}>
                          <span
                            className={`status-badge clickable ${
                              item.isActive ? 'status-active' : 'status-inactive'
                            }`}
                            onClick={() => handleStatusToggle(item)}
                            title={`Click to ${item.isActive ? 'deactivate' : 'activate'}`}
                          >
                            {item.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </PermissionGuard>
                        {!can(PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW) && (
                          <span className={`status-badge ${item.isActive ? 'status-active' : 'status-inactive'}`}>
                            {item.isActive ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td>
                        <UserTimestamp
                          userId={item.updatedBy}
                          dateTime={item.updatedAt}
                          action="updated"
                          getUserName={getUserName}
                          getUserById={getUserById}
                        />
                      </td>
                      <td style={{ textAlign: 'right', paddingRight: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
                          <PermissionGuard permission={PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                              <Edit
                                size={18}
                                style={{
                                  color: '#3b82f6',
                                  cursor: 'pointer',
                                  transition: 'color 0.2s'
                                }}
                                onClick={() => handleEditClick(item)}
                                onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                                onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
                                title="Edit master data"
                              />
                            </div>
                          </PermissionGuard>
                          <PermissionGuard permission={PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW}>
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={() => handleDeleteClick(item)}
                              disabled={item.isActive}
                              title={item.isActive ? 'Deactivate first to delete' : 'Delete'}
                              style={{
                                opacity: item.isActive ? 0.4 : 1,
                                cursor: item.isActive ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <Trash2 size={18} style={{ color: '#ef4444' }} />
                            </button>
                          </PermissionGuard>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Master Data Modal */}
      {showAddModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!isSubmitting) {
              setShowAddModal(false);
              resetForm();
            }
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editMode ? 'Edit Master Data' : 'Add New Master Data'}</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                disabled={isSubmitting}
              >
                <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form master-data-modal-form">
              <div className="form-group">
                <label htmlFor="metadataType">Type *</label>
                <select
                  id="metadataType"
                  name="metadataType"
                  value={formData.metadataType}
                  onChange={handleInputChange}
                  disabled={isSubmitting || editMode || typesLoading}
                  required
                >
                  <option value="">
                    {typesLoading ? 'Loading types...' : 'Select type'}
                  </option>
                  {supportedMetadataTypes.map((value) => (
                    <option key={value} value={value}>
                      {formatMetadataType(value)}
                    </option>
                  ))}
                </select>
                {formErrors.metadataType && <span className="error-text">{formErrors.metadataType}</span>}
                {!editMode && !typesLoading && supportedMetadataTypes.length === 0 && (
                  <span className="form-hint">No types available. Refresh the page or contact support.</span>
                )}
                {editMode && <span className="form-hint">Type cannot be changed</span>}
              </div>

              <div className="form-group">
                <label htmlFor="code">Code *</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleCodeChange}
                  placeholder="e.g. DIT_DRL"
                  disabled={isSubmitting || editMode}
                  required
                />
                {formErrors.code && <span className="error-text">{formErrors.code}</span>}
                <span className="form-hint">{editMode ? 'Code cannot be changed' : 'Uppercase letters and underscore only'}</span>
              </div>

              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter name"
                  disabled={isSubmitting}
                  required
                />
                {formErrors.name && <span className="error-text">{formErrors.name}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="sortOrder">Sort Order (optional)</label>
                  <input
                    type="number"
                    id="sortOrder"
                    name="sortOrder"
                    value={formData.sortOrder}
                    onChange={handleInputChange}
                    placeholder="e.g. 10"
                    min="0"
                    disabled={isSubmitting}
                  />
                  {formErrors.sortOrder && <span className="error-text">{formErrors.sortOrder}</span>}
                </div>
                <div className="form-group master-data-active-group">
                  <label className="master-data-checkbox">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      disabled={isSubmitting || editMode}
                    />
                    <span>Active</span>
                  </label>
                  {editMode && <span className="form-hint" style={{ fontSize: '12px', marginTop: '4px' }}>Use status toggle to change</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                  disabled={isSubmitting}
                  rows={2}
                  required
                />
                {formErrors.description && <span className="error-text">{formErrors.description}</span>}
              </div>

              {formErrors.submit && (
                <div className="error-text" style={{ marginBottom: 12 }}>
                  {formErrors.submit}
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Status Toggle Confirmation Modal */}
      {showStatusModal && statusUpdateItem && (
        <div
          className="modal-overlay"
          onClick={() => !isUpdatingStatus && setShowStatusModal(false)}
        >
          <div className="modal-content status-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Status Update</h2>
              <button
                className="modal-close"
                onClick={() => setShowStatusModal(false)}
                disabled={isUpdatingStatus}
              >
                <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="status-modal-body">
              <p className="status-modal-message">
                {statusUpdateItem.isActive ? (
                  <>
                    Are you sure you want to{' '}
                    <span className="status-text-badge status-text-deactivate">deactivate</span>{' '}
                    <strong>{statusUpdateItem.name}</strong>?
                  </>
                ) : (
                  <>
                    Are you sure you want to{' '}
                    <span className="status-text-badge status-text-activate">activate</span>{' '}
                    <strong>{statusUpdateItem.name}</strong>?
                  </>
                )}
              </p>

              <div className="status-modal-actions">
                <button
                  type="button"
                  className="status-btn-no"
                  onClick={() => setShowStatusModal(false)}
                  disabled={isUpdatingStatus}
                >
                  No
                </button>
                <button
                  type="button"
                  className={`status-btn-yes ${
                    !statusUpdateItem.isActive ? 'status-btn-activate' : 'status-btn-deactivate'
                  }`}
                  onClick={handleConfirmStatusUpdate}
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? 'Updating...' : 'Yes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteItem && (
        <div
          className="modal-overlay"
          onClick={() => !isDeleting && setShowDeleteModal(false)}
        >
          <div className="modal-content status-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="status-modal-body">
              <p className="status-modal-message">
                Are you sure you want to permanently delete <strong>{deleteItem.name}</strong> ({deleteItem.code})?
              </p>

              <div className="status-modal-actions">
                <button
                  type="button"
                  className="status-btn-no"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="status-btn-yes status-btn-deactivate"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterData;
